import appConfig from '../../config';
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

import {
  Menu,
  X,
  LayoutDashboard,
  Users,
  CreditCard,
  History,
  LogOut,
  ArrowUpCircle,
  ArrowDownCircle,
  Percent,
  Bell,
  User,
  PlusCircle,
  List,
  ChevronDown,
  ChevronRight,
  Link,
  FileBarChart2,
  Settings,
  FolderKanban,
  FileText,
} from "lucide-react";


interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  toggleSidebar,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentRole = (
    localStorage.getItem("role") || ""
  ).toLowerCase();

  const [paymentExpanded, setPaymentExpanded] =
    useState(false);

  const [settingExpanded, setSettingExpanded] =
    useState(false);

  const [cmsExpanded, setCmsExpanded] =
    useState(false);

  const [offersExpanded, setOffersExpanded] =
    useState(false);

  const [gameEngineExpanded, setGameEngineExpanded] =
    useState(false);

  const [teamExpanded, setTeamExpanded] = useState(false);

  const [reportsExpanded, setReportsExpanded] =
    useState(false);

  const [payoutsExpanded, setPayoutsExpanded] = useState(false);

  // Auto-expand menus based on current path
  useEffect(() => {
    if (pathname.includes('affiliate-payouts')) {
      setPayoutsExpanded(true);
    }
    if (pathname.startsWith('/cms')) {
      setCmsExpanded(true);
    }
    if (pathname.includes('campaigns')) {
      setOffersExpanded(true);
    }
    if (pathname.includes('withdraw') || pathname.includes('deposit')) {
      setPaymentExpanded(true);
    }
    if (pathname.includes('settings') || pathname.includes('auth-configs') || pathname.includes('gateway-configs')) {
      setSettingExpanded(true);
    }
    if (pathname.includes('game-engine')) {
      setGameEngineExpanded(true);
    }
    if (pathname.includes('reports')) {
      setReportsExpanded(true);
    }
    if (pathname.includes('subadmin') || pathname.includes('collaborator')) {
      setTeamExpanded(true);
    }
  }, [pathname]);

  // ===  Brand from DB (admin panel settings) ===
  const [siteName, setSiteName] = useState('WinSmash');
  const [smallLogo, setSmallLogo] = useState<string | null>(null);
  const [bigLogo,   setBigLogo]   = useState<string | null>(null);

  useEffect(() => {
    const apply = (payload: any) => {
      const data = payload?.success ? payload.data : payload;
      if (data?.website_name) setSiteName(data.website_name);
      if (data?.logo_small)   setSmallLogo(`${appConfig.baseURL}${data.logo_small}?t=${Date.now()}`);
      if (data?.logo_big)     setBigLogo(`${appConfig.baseURL}${data.logo_big}?t=${Date.now()}`);
    };

    // Reuse already-fetched data from App.tsx if available
    if ((window as any).adminSiteConfig) {
      apply((window as any).adminSiteConfig);
    } else {
      fetch(`${appConfig.baseURL}/website-config`)
        .then(r => r.json())
        .then(apply)
        .catch(() => {});
    }
  }, []);
  // =============================================

  const sidebarRef =
    useRef<HTMLDivElement>(null);

  // =========================
  // ROLE BASE ACCESS
  // =========================
  const permissions = {
    dashboard: [
      "admin",
      "manager",
      "affiliate",
      "subadmin",
    ],

    users: ["admin", "manager", "affiliate", "subadmin"],

    notifications: ["admin"],

    reports: [
      "admin",
      "manager",
      "affiliate",
    ],

    subadmin: ["admin"],

    collaborator: [
      "admin",
      "manager",
    ],

    collaboratorPending: [
      "admin",
      "",
    ],

    affiliate: [
      "admin",
      "manager",
      "affiliate",
    ],

    payment: ["admin", "manager", "affiliate"],

    settings: ["admin"],

    gameEngine: ["admin"],

    payoutRules: ["admin", "manager", "affiliate"],
  };

  const hasAccess = (
    roles: string[]
  ) => {
    return roles.includes(currentRole);
  };

  const handleLogout = () => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    localStorage.clear();
    if (role === "affiliate") {
      navigate("/login/affiliate");
    } else if (role === "subadmin" || role === "manager") {
      navigate("/login/manager");
    } else {
      navigate("/login");
    }
  };

  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent
    ) => {
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(
          event.target as Node
        )
      ) {
        if (window.innerWidth < 1024) {
          toggleSidebar();
        }
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [isOpen, toggleSidebar]);

  const menuClass = ({ isActive }: { isActive: boolean }) =>
    `
      group
      flex
      items-center
      gap-2.5
      px-2.5
      py-2
      rounded-md
      text-[13px]
      transition-all
      duration-200
      mb-0.5
      ${isActive
      ? "bg-white/10 text-white font-bold"
      : "text-slate-400 hover:bg-slate-800 hover:text-white"
    }
    `;

  const iconClass =
    "w-4 h-4 shrink-0 transition-colors group-hover:text-white opacity-80";

  return (
    <>
      {/* Mobile button */}
      {isOpen && (
        <button
          className="fixed top-4 left-4 z-[1000] text-white lg:hidden"
          onClick={toggleSidebar}
        >
          <X size={22} />
        </button>
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`
          w-[260px]
          h-screen
          bg-slate-900
          border-r border-slate-800
          flex flex-col shrink-0
          fixed top-0 left-0 z-[999]
          shadow-xl lg:shadow-none
          transition-all duration-300
          ${isOpen
            ? "translate-x-0"
            : "-translate-x-full"
          }
          lg:translate-x-0
        `}
      >
        {/* Brand Logo — name from DB */}
        <div className="flex items-center justify-between px-3 h-14 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-white flex items-center justify-center shadow shadow-white/10 overflow-hidden">
              {smallLogo
                ? <img src={smallLogo} alt={siteName} className="w-full h-full object-contain" />
                : <span className="text-slate-900 font-bold text-sm">{siteName.charAt(0).toUpperCase()}</span>
              }
            </div>
            <div className="flex flex-col">
              <span className="text-white font-black text-xs tracking-tighter uppercase leading-none">
                {siteName}
              </span>
              <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-0.5">{currentRole || 'Admin'}</span>
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="flex-1 overflow-y-auto px-2 py-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">

          {/* Dashboard */}
          {hasAccess(
            permissions.dashboard
          ) && (
              <NavLink
                to="/"
                className={menuClass}
              >
                <LayoutDashboard
                  size={18}
                  className={iconClass}
                />
                Dashboard
              </NavLink>
            )}


          {/* Team Management Dropdown */}
          {(hasAccess(permissions.subadmin) || hasAccess(permissions.collaborator) || hasAccess(permissions.collaboratorPending)) && (
            <>
              <button
                onClick={() => setTeamExpanded(!teamExpanded)}
                className="
                  w-full
                  flex
                  items-center
                  justify-between
                  px-2.5
                  py-2
                  rounded-md
                  text-[13px]
                  mb-0.5
                  text-slate-400 hover:bg-slate-800 hover:text-white transition-all duration-200 group
                "
              >
                <div className="flex items-center gap-2.5">
                  <Users
                    className={iconClass}
                  />
                  Team Management
                </div>

                {teamExpanded ? (
                  <ChevronDown size={14} className="opacity-50" />
                ) : (
                  <ChevronRight size={14} className="opacity-50" />
                )}
              </button>

              {teamExpanded && (
                <div className="mt-0.5 space-y-0.5 animate-in slide-in-from-left-1 duration-200">
                  {hasAccess(permissions.subadmin) && (
                    <NavLink
                      to="/subadmin"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      Manager
                    </NavLink>
                  )}
                  {hasAccess(permissions.collaborator) && (
                    <NavLink
                      to="/collaboratorUser"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      Affiliate
                    </NavLink>
                  )}
                  {hasAccess(permissions.collaboratorPending) && (
                    <NavLink
                      to="/pendingCollaboratorUser"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      Pending Affiliate
                    </NavLink>
                  )}
                </div>
              )}
            </>
          )}


          {hasAccess(
            permissions.users
          ) && (
              <NavLink
                to="/users"
                className={menuClass}
              >
                <Users
                  size={18}
                  className={iconClass}
                />
                Users
              </NavLink>
            )}

          {/* Notifications */}
          {hasAccess(
            permissions.notifications
          ) && (
              <NavLink
                to="/notifications"
                className={menuClass}
              >
                <Bell
                  size={18}
                  className={iconClass}
                />
                Notifications
              </NavLink>
            )}

          {/* Offers Management Dropdown */}
          {hasAccess(permissions.notifications) && (
            <>
              <button
                onClick={() => setOffersExpanded(!offersExpanded)}
                className="
                  w-full
                  flex
                  items-center
                  justify-between
                  px-2.5
                  py-2
                  rounded-md
                  text-[13px]
                  mb-0.5
                  text-slate-400 hover:bg-slate-800 hover:text-white transition-all duration-200 group
                "
              >
                <div className="flex items-center gap-2.5">
                  <Percent
                    className={iconClass}
                  />
                  Offers Management
                </div>

                {offersExpanded ? (
                  <ChevronDown size={14} className="opacity-50" />
                ) : (
                  <ChevronRight size={14} className="opacity-50" />
                )}
              </button>

              {offersExpanded && (
                <div className="mt-0.5 space-y-0.5 animate-in slide-in-from-left-1 duration-200">
                  <NavLink
                    to="/campaigns"
                    className={({ isActive }) => {
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Campaign Builder
                  </NavLink>
                  <NavLink
                    to="/affiliate-settings"
                    className={({ isActive }) => {
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Affiliate Settings
                  </NavLink>
                  <NavLink
                    to="/vip-levels"
                    className={({ isActive }) => {
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    VIP Levels
                  </NavLink>
                </div>
              )}
            </>
          )}

          {/* Affiliate Payouts Dropdown */}
          {hasAccess(permissions.payoutRules) && (
            <>
              <button
                onClick={() => setPayoutsExpanded(!payoutsExpanded)}
                className="
                  w-full
                  flex
                  items-center
                  justify-between
                  px-2.5
                  py-2
                  rounded-md
                  text-[13px]
                  mb-0.5
                  text-slate-400 hover:bg-slate-800 hover:text-white transition-all duration-200 group
                "
              >
                <div className="flex items-center gap-2.5">
                  <CreditCard className={iconClass} />
                  Affiliate Payouts
                </div>

                {payoutsExpanded ? (
                  <ChevronDown size={14} className="opacity-50" />
                ) : (
                  <ChevronRight size={14} className="opacity-50" />
                )}
              </button>

               {payoutsExpanded && (
                <div className="mt-0.5 space-y-0.5 animate-in slide-in-from-left-1 duration-200">
                  <NavLink
                    to="/affiliate-payouts/rules"
                    className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                  >
                    Payout Rules
                  </NavLink>
                  <NavLink
                    to="/affiliate-payouts/report"
                    className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                  >
                    Payout Report
                  </NavLink>
                </div>
              )}
            </>
          )}

          {/* CMS Management Dropdown */}
          {hasAccess(permissions.settings) && (
            <>
              <button
                onClick={() => setCmsExpanded(!cmsExpanded)}
                className="
                  w-full
                  flex
                  items-center
                  justify-between
                  px-2.5
                  py-2
                  rounded-md
                  text-[13px]
                  mb-0.5
                  text-slate-400 hover:bg-slate-800 hover:text-white transition-all duration-200 group
                "
              >
                <div className="flex items-center gap-2.5">
                  <FileText
                    className={iconClass}
                  />
                  CMS Management
                </div>

                {cmsExpanded ? (
                  <ChevronDown size={14} className="opacity-50" />
                ) : (
                  <ChevronRight size={14} className="opacity-50" />
                )}
              </button>

              {cmsExpanded && (
                <div className="mt-0.5 space-y-0.5 animate-in slide-in-from-left-1 duration-200">
                  <NavLink
                    to="/cms?tab=pages"
                    className={({ isActive }) => {
                      const isTabActive = isActive && (new URLSearchParams(window.location.search).get('tab') === 'pages' || !new URLSearchParams(window.location.search).get('tab'));
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isTabActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Pages Content
                  </NavLink>
                  <NavLink
                    to="/cms?tab=banners"
                    className={({ isActive }) => {
                      const isTabActive = isActive && new URLSearchParams(window.location.search).get('tab') === 'banners';
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isTabActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Sliders & Banners
                  </NavLink>
                  <NavLink
                    to="/cms?tab=logos"
                    className={({ isActive }) => {
                      const isTabActive = isActive && new URLSearchParams(window.location.search).get('tab') === 'logos';
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isTabActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Logos & Branding
                  </NavLink>
                  <NavLink
                    to="/cms?tab=footer"
                    className={({ isActive }) => {
                      const isTabActive = isActive && new URLSearchParams(window.location.search).get('tab') === 'footer';
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isTabActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Footer Settings
                  </NavLink>
                  <NavLink
                    to="/cms?tab=social"
                    className={({ isActive }) => {
                      const isTabActive = isActive && new URLSearchParams(window.location.search).get('tab') === 'social';
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isTabActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Social Links
                  </NavLink>
                  <NavLink
                    to="/cms?tab=help"
                    className={({ isActive }) => {
                      const isTabActive = isActive && new URLSearchParams(window.location.search).get('tab') === 'help';
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isTabActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Help Center
                  </NavLink>
                  <NavLink
                    to="/cms?tab=game_cards"
                    className={({ isActive }) => {
                      const isTabActive = isActive && new URLSearchParams(window.location.search).get('tab') === 'game_cards';
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isTabActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Home Game Cards
                  </NavLink>
                  <NavLink
                    to="/cms?tab=affiliate"
                    className={({ isActive }) => {
                      const isTabActive = isActive && new URLSearchParams(window.location.search).get('tab') === 'affiliate';
                      return `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isTabActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`;
                    }}
                  >
                    Affiliate CMS
                  </NavLink>
                </div>
              )}
            </>
          )}


          {/* Reports & Analytics */}
          {hasAccess(permissions.reports) && (
            <>
              <button
                onClick={() => setReportsExpanded(!reportsExpanded)}
                className="w-full flex items-center justify-between px-2.5 py-2 rounded-md text-[13px] mb-0.5 text-slate-400 hover:bg-slate-800 hover:text-white transition-all duration-200 group"
              >
                <div className="flex items-center gap-2.5">
                  <FileBarChart2 size={18} className={iconClass} />
                  Analytics & Reports
                </div>
                {reportsExpanded ? (
                  <ChevronDown size={14} className="opacity-50" />
                ) : (
                  <ChevronRight size={14} className="opacity-50" />
                )}
              </button>

              {reportsExpanded && (
                <div className="mb-2 space-y-0.5 overflow-hidden">
                  <NavLink
                    to="/reports/users"
                    className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                  >
                    User Reports
                  </NavLink>
                  {currentRole !== 'affiliate' && (
                    <NavLink
                      to="/reports/financials"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      Financial & Bonus Logs
                    </NavLink>
                  )}
                  <NavLink
                    to="/reports/game-history"
                    className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                  >
                    Global Game History
                  </NavLink>
                  {currentRole !== 'manager' && currentRole !== 'affiliate' && (
                    <NavLink
                      to="/reports/profit"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      GGR & Analytics
                    </NavLink>
                  )}
                </div>
              )}
            </>
          )}


          {/* Affiliate */}
          {/* {hasAccess(
            permissions.affiliate
          ) && (
              <NavLink
                to="/affiliate"
                className={menuClass}
              >
                <Link
                  size={18}
                  className={iconClass}
                />
                Affiliate
              </NavLink>
            )} */}

          {/* Payment Dropdown */}
          {hasAccess(
            permissions.payment
          ) && (
              <>
                <button
                  onClick={() =>
                    setPaymentExpanded(
                      !paymentExpanded
                    )
                  }
                  className="
                  w-full
                  flex
                  items-center
                  justify-between
                  px-2.5
                  py-2
                  rounded-md
                  text-[13px]
                  mb-0.5
                  text-slate-400 hover:bg-slate-800 hover:text-white transition-all duration-200 group
                "
                >
                  <div className="flex items-center gap-2.5">
                    <CreditCard
                      className={iconClass}
                    />
                    Manage Payment
                  </div>

                  {paymentExpanded ? (
                    <ChevronDown size={14} className="opacity-50" />
                  ) : (
                    <ChevronRight size={14} className="opacity-50" />
                  )}
                </button>

                {paymentExpanded && (
                  <div className="mt-0.5 space-y-0.5 animate-in slide-in-from-left-1 duration-200">
                    <NavLink
                      to="/withdrawals"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      Withdrawal
                    </NavLink>

                    <NavLink
                      to="/deposits"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      Deposit
                    </NavLink>
                  </div>
                )}
              </>
            )}

          {/* Settings Dropdown */}
          {hasAccess(
            permissions.settings
          ) && (
              <>
                <button
                  onClick={() =>
                    setSettingExpanded(
                      !settingExpanded
                    )
                  }
                  className="
                  w-full
                  flex
                  items-center
                  justify-between
                  px-2.5
                  py-2
                  rounded-md
                  text-[13px]
                  mb-0.5
                  text-slate-400 hover:bg-slate-800 hover:text-white transition-all duration-200 group
                "
                >
                  <div className="flex items-center gap-2.5">
                    <Settings
                      className={iconClass}
                    />
                    Settings
                  </div>

                  {settingExpanded ? (
                    <ChevronDown size={14} className="opacity-50" />
                  ) : (
                    <ChevronRight size={14} className="opacity-50" />
                  )}
                </button>

                {settingExpanded && (
                  <div className="mt-0.5 space-y-0.5 animate-in slide-in-from-left-1 duration-200">

                    <NavLink
                      to="/settings"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      Website Setup
                    </NavLink>
                    <NavLink
                      to="/auth-configs"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      Auth Requirements
                    </NavLink>
                    <NavLink
                      to="/gateway-configs"
                      className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                    >
                      Gateway Keys
                    </NavLink>
                  </div>
                )}
              </>
            )}

          {/* Game Engine Dropdown */}
          {hasAccess(permissions.gameEngine) && (
            <>
              <button
                onClick={() => setGameEngineExpanded(!gameEngineExpanded)}
                className="
                  w-full
                  flex
                  items-center
                  justify-between
                  px-2.5
                  py-2
                  rounded-md
                  text-[13px]
                  mb-0.5
                  text-slate-400 hover:bg-slate-800 hover:text-white transition-all duration-200 group
                "
              >
                <div className="flex items-center gap-2.5">
                  <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  Game Engine
                </div>
                {gameEngineExpanded ? (
                  <ChevronDown size={14} className="opacity-50" />
                ) : (
                  <ChevronRight size={14} className="opacity-50" />
                )}
              </button>

              {gameEngineExpanded && (
                <div className="mt-0.5 space-y-0.5 animate-in slide-in-from-left-1 duration-200">
                  <NavLink to="/game-engine/aggregators" className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>Aggregators</NavLink>
                  <NavLink to="/game-engine/providers" className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>Providers</NavLink>
                  <NavLink to="/game-engine/categories" className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>Categories</NavLink>
                  <NavLink to="/game-engine/groups" className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>Groups</NavLink>
                  <NavLink to="/game-engine/games" className={({ isActive }) => `w-full block text-left pl-9 pr-2 py-1.5 text-[12px] font-medium transition-colors rounded-md ${isActive ? 'text-white bg-white/5 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>Games</NavLink>
                </div>
              )}
            </>
          )}


        </div>

        {/* Logout */}
        <div className="p-4 shrink-0 mt-auto border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="
              w-full
              flex
              items-center
              justify-center
              py-2.5
              px-4
              rounded-xl
              text-slate-400 hover:text-white
              hover:bg-slate-800 transition-colors
            "
          >
            <LogOut
              size={18}
              className="mr-3"
            />
            Log out
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;